import { useEffect, useState } from "react";
import axios from "axios";
import Loading from "../../../components/utils/Loading.js";
import { formattedDate } from "../../../components/formatted/FormattedDateAndTime.js";
import { getStatisticsCo2Generated } from "../../../services/container.js";
import { useTranslation } from "react-i18next";
import { WarningComponent } from "../../../components/WarningComponent.js";
import InfoTip from "../../../components/InfoTip.js";
import { ReactComponent as HelpIcon } from "../../../static/assets/dashboard-help.svg";

const StatisticsKeyValuesComponentCo2 = ({
  periodHistoryStart,
  periodHistoryStop,
  periodHistoryStartPrevious,
  periodHistoryStopPrevious,
  enablePreviousPeriod,
  keywordFilter,
  handleValidate,
  setHandleValidate,
}) => {
  const { t } = useTranslation();
  const [co2Generated, setCo2Generated] = useState(0);
  const [co2GeneratedPrevious, setCo2GeneratedPrevious] = useState(0);
  const [isCo2GeneratedPreviousPeriod, setIsCo2GeneratedPreviousPeriod] =
    useState(0);
  const [percentageCo2Generated, setPercentageCo2Generated] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isRequiredWarning, setIsRequiredWarning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(
    () => {
      setHandleValidate(true);
    },
    // eslint-disable-next-line
    [],
  );

  const fetchData = async () => {
    setIsLoading(true);
    const keywordValue = keywordFilter?.value ?? [];

    const previousCo2Request =
      enablePreviousPeriod &&
      periodHistoryStartPrevious &&
      periodHistoryStopPrevious
        ? getStatisticsCo2Generated({
            periodStart: periodHistoryStartPrevious,
            periodStop: periodHistoryStopPrevious,
            keywordFilter: JSON.stringify(keywordValue),
          })
        : Promise.resolve({
            status: 200,
            data: { co2Generated: 0 },
          });

    await axios
      .all([
        getStatisticsCo2Generated({
          periodStart: periodHistoryStart,
          periodStop: periodHistoryStop,
          keywordFilter: JSON.stringify(keywordValue),
        }),
        previousCo2Request,
      ])
      .then(
        axios.spread((responseCo2Generated, responseCo2GeneratedPrevious) => {
          if (
            responseCo2Generated.status === 200 &&
            responseCo2GeneratedPrevious.status === 200
          ) {
            const currentValue = responseCo2Generated.data?.co2Generated ?? 0;
            const previousValue =
              responseCo2GeneratedPrevious.data?.co2Generated ?? 0;

            setCo2Generated(currentValue);

            if (
              !enablePreviousPeriod ||
              responseCo2GeneratedPrevious.data?.error
            ) {
              setCo2GeneratedPrevious(0);
            } else {
              setCo2GeneratedPrevious(previousValue);
            }

            const hasPreviousPeriodData =
              enablePreviousPeriod && previousValue > 0;
            if (hasPreviousPeriodData) {
              setPercentageCo2Generated(
                Math.round(
                  ((currentValue - previousValue) / previousValue) * 100,
                ),
              );
            } else {
              setPercentageCo2Generated(0);
            }

            setIsCo2GeneratedPreviousPeriod(
              enablePreviousPeriod && previousValue > 0,
            );

            if (responseCo2Generated.data.error) {
              setCo2Generated(0);
              const errorMessage = t(
                "Destination sites and/or densities are missing. Please configure them in your address and container settings.",
              );
              setErrorMessage(errorMessage);
            }
          }
        }),
      )
      .catch((err) => {
        if (err.response.status === 400) {
          if (err.response.data.error === "No containers completed") {
            const errorMessage = t("configure_destination_addresses");
            setErrorMessage(errorMessage);
            setIsRequiredWarning(true);
          }
        }
      });
    setIsLoading(false);
    setHandleValidate(false);
  };

  useEffect(
    () => {
      if (handleValidate) {
        fetchData();
      }
    },
    // eslint-disable-next-line
    [handleValidate],
  );

  const showTrend =
    enablePreviousPeriod &&
    isCo2GeneratedPreviousPeriod &&
    Number.isFinite(percentageCo2Generated);
  const trendIsImprovement = showTrend && percentageCo2Generated < 0;

  return (
    <div>
      {!isLoading ? (
        isRequiredWarning || errorMessage ? (
          <article className="statistics-card w-full grid justify-items-center p-5">
            <WarningComponent required={true} message={errorMessage} />
          </article>
        ) : (
          <article className="flex flex-row flex-wrap w-full gap-6">
            <div className="statistics-card flex flex-col flex-1 min-w-[240px] gap-3">
              <div className="flex flex-row items-center gap-3">
                <span className="inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl bg-[#E9FBFB] text-[#00A7A6]">
                  <i className="fas fa-cloud text-xl" aria-hidden />
                </span>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1 text-gray-600">
                    <span>
                      CO<sub>2</sub> {t("produced")}
                    </span>
                    <InfoTip icon={HelpIcon}>
                      {t("date.range", {
                        from: formattedDate(periodHistoryStart, t),
                        to: formattedDate(periodHistoryStop, t),
                      })}
                    </InfoTip>
                  </div>
                  <h3 className="font-bold text-xl text-[#000000]">
                    {co2Generated === 0
                      ? "–"
                      : `${Math.round(co2Generated)} kg`}
                  </h3>
                </div>
              </div>
              {showTrend ? (
                <div
                  className={`self-start flex flex-row items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                    percentageCo2Generated === 0
                      ? "bg-gray-100 text-gray-500"
                      : trendIsImprovement
                        ? "bg-[#E9FBFB] text-[#008685]"
                        : "bg-[#FDECEF] text-[#E5193C]"
                  }`}
                >
                  <span className="font-semibold">
                    {percentageCo2Generated > 0 ? "+" : ""}
                    {percentageCo2Generated}%
                  </span>
                  <span>
                    {t("vs previous period")}
                    {co2GeneratedPrevious > 0
                      ? ` (${Math.round(co2GeneratedPrevious)} kg)`
                      : ""}
                  </span>
                </div>
              ) : null}
            </div>
          </article>
        )
      ) : (
        <div>
          <Loading height={"35vh"} />
        </div>
      )}
    </div>
  );
};

export default StatisticsKeyValuesComponentCo2;
